import React, { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import { Document, Page, pdfjs } from 'react-pdf';
import { toast } from 'sonner';
import {
  FileSignature,
  Upload,
  Plus,
  Trash2,
  Send,
  Eye,
  Users,
  FileText,
  CheckCircle2,
  Clock,
  AlertCircle,
  X,
  ChevronLeft,
  ChevronRight,
  Loader2,
  PenLine,
  Mail,
  MousePointer,
  Copy,
  Search,
  FileWarning
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const API = process.env.REACT_APP_BACKEND_URL + '/api/hr';

const SIGNER_COLORS = [
  '#10b981', '#3b82f6', '#f59e0b', '#ef4444',
  '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'
];

export default function Documents() {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('awaiting');
  const [searchQuery, setSearchQuery] = useState('');

  // Create document flow
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [documentTitle, setDocumentTitle] = useState('');
  const [documentType, setDocumentType] = useState('contract');
  const [documentMessage, setDocumentMessage] = useState('');

  // PDF viewer state
  const [currentPage, setCurrentPage] = useState(1);
  const [numPages, setNumPages] = useState(0);
  const [pageSize, setPageSize] = useState({ width: 612, height: 792 });

  // Signature fields
  const [signatureFields, setSignatureFields] = useState([]);
  const [showSignerPopup, setShowSignerPopup] = useState(false);
  const [pendingField, setPendingField] = useState(null);
  const [newSignerName, setNewSignerName] = useState('');
  const [newSignerEmail, setNewSignerEmail] = useState('');

  // View document
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [viewPage, setViewPage] = useState(1);
  const [viewNumPages, setViewNumPages] = useState(0);
  const [pdfError, setPdfError] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(true);
  const [documentSearch, setDocumentSearch] = useState('');

  // Actions
  const [sending, setSending] = useState(false);
  const [creating, setCreating] = useState(false);
  const [showSignLinksModal, setShowSignLinksModal] = useState(false);
  const [signLinks, setSignLinks] = useState([]);

  const fileInputRef = useRef(null);
  const pdfContainerRef = useRef(null);

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('hrToken');
      const response = await axios.get(`${API}/documents`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDocuments(response.data);
    } catch (err) {
      console.error('Error fetching documents:', err);
    } finally {
      setLoading(false);
    }
  };

  // Filter documents
  const filteredDocuments = useMemo(() => {
    let filtered = documents;

    if (activeTab === 'awaiting') {
      filtered = filtered.filter(d => d.status === 'sent' || d.status === 'partially_signed');
    } else if (activeTab === 'completed') {
      filtered = filtered.filter(d => d.status === 'completed');
    } else if (activeTab === 'draft') {
      filtered = filtered.filter(d => d.status === 'draft');
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(d =>
        d.title?.toLowerCase().includes(query) ||
        d.document_type?.toLowerCase().includes(query) ||
        d.signers?.some(s =>
          s.name?.toLowerCase().includes(query) ||
          s.email?.toLowerCase().includes(query)
        )
      );
    }

    return filtered;
  }, [documents, activeTab, searchQuery]);

  const stats = {
    awaiting: documents.filter(d => d.status === 'sent' || d.status === 'partially_signed').length,
    completed: documents.filter(d => d.status === 'completed').length,
    draft: documents.filter(d => d.status === 'draft').length
  };

  // File upload
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'image/png', 'image/jpeg', 'image/jpg', 'image/gif'
    ];

    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a PDF, DOC, DOCX, or image file');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('hrToken');
      const response = await axios.post(`${API}/documents/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setUploadedFile(response.data);
      setDocumentTitle(file.name.replace(/\.[^/.]+$/, ''));
      setNumPages(response.data.page_count);
      if (response.data.pages?.[0]) {
        setPageSize({
          width: response.data.pages[0].width,
          height: response.data.pages[0].height
        });
      }
      toast.success('Document uploaded! Click to add signature fields.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload');
    } finally {
      setUploading(false);
    }
  };

  const handlePdfClick = (e) => {
    if (!uploadedFile || !pdfContainerRef.current) return;

    const rect = pdfContainerRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;

    setPendingField({ x, y, page: currentPage });
    setNewSignerName('');
    setNewSignerEmail('');
    setShowSignerPopup(true);
  };

  const handleAddSignatureField = () => {
    if (!newSignerName.trim() || !newSignerEmail.trim()) {
      toast.error('Please enter signer name and email');
      return;
    }

    if (!newSignerEmail.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }

    const colorIndex = signatureFields.length % SIGNER_COLORS.length;

    const newField = {
      id: `field-${Date.now()}`,
      signerName: newSignerName.trim(),
      signerEmail: newSignerEmail.trim(),
      color: SIGNER_COLORS[colorIndex],
      page: pendingField.page,
      x: pendingField.x,
      y: pendingField.y,
      width: 20,
      height: 8
    };

    setSignatureFields([...signatureFields, newField]);
    setShowSignerPopup(false);
    setPendingField(null);
    toast.success(`Added signature field for ${newSignerName}`);
  };

  const removeSignatureField = (fieldId) => {
    setSignatureFields(signatureFields.filter(f => f.id !== fieldId));
  };

  const handleCreateDocument = async (sendImmediately = false) => {
    if (!uploadedFile || !documentTitle.trim()) {
      toast.error('Please upload a document and enter a title');
      return;
    }

    if (signatureFields.length === 0) {
      toast.error('Please add at least one signature field');
      return;
    }

    setCreating(true);

    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('hrToken');

      const signerMap = {};
      signatureFields.forEach(field => {
        if (!signerMap[field.signerEmail]) {
          signerMap[field.signerEmail] = {
            name: field.signerName,
            email: field.signerEmail,
            color: field.color,
            signature_fields: []
          };
        }
        signerMap[field.signerEmail].signature_fields.push({
          page: field.page,
          x: field.x,
          y: field.y,
          width: field.width,
          height: field.height
        });
      });

      const signersData = Object.values(signerMap);

      const formData = new FormData();
      formData.append('title', documentTitle);
      formData.append('document_type', documentType);
      formData.append('message', documentMessage);
      formData.append('file_id', uploadedFile.file_id);
      formData.append('pdf_filename', uploadedFile.pdf_filename);
      formData.append('signers', JSON.stringify(signersData));

      const createResponse = await axios.post(`${API}/documents/with-pdf`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      const documentId = createResponse.data.document_id;

      if (sendImmediately) {
        const sendResponse = await axios.post(
          `${API}/documents/${documentId}/send`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const links = sendResponse.data.email_results || [];
        setSignLinks(links);
        setShowSignLinksModal(true);
        toast.success(`Document sent to ${links.length} signer(s)!`);
      } else {
        toast.success('Document saved as draft');
      }

      resetCreateModal();
      fetchDocuments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create document');
    } finally {
      setCreating(false);
    }
  };

  const handleSendDocument = async (documentId) => {
    setSending(true);
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('hrToken');
      const response = await axios.post(
        `${API}/documents/${documentId}/send`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const links = response.data.email_results || [];
      setSignLinks(links);
      setShowSignLinksModal(true);
      toast.success(`Document sent!`);
      fetchDocuments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  const handleDeleteDocument = async (documentId) => {
    if (!window.confirm('Delete this document?')) return;

    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('hrToken');
      await axios.delete(`${API}/documents/${documentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Document deleted');
      fetchDocuments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const viewDocument = async (doc) => {
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('hrToken');
      const response = await axios.get(`${API}/documents/${doc.document_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedDocument(response.data);
      setViewPage(1);
      setViewNumPages(0);
      setPdfError(false);
      setPdfLoading(true);
      setDocumentSearch('');
      setShowViewModal(true);
    } catch (err) {
      toast.error('Failed to load document');
    }
  };

  const resetCreateModal = () => {
    setShowCreateModal(false);
    setUploadedFile(null);
    setDocumentTitle('');
    setDocumentType('contract');
    setDocumentMessage('');
    setSignatureFields([]);
    setCurrentPage(1);
    setNumPages(0);
    setPendingField(null);
    setShowSignerPopup(false);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Link copied!');
  };

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };

  const onPageLoadSuccess = (page) => {
    setPageSize({ width: page.width, height: page.height });
  };

  // Get the correct PDF URL for viewing
  const getViewPdfUrl = () => {
    if (!selectedDocument) return null;
    
    // Use signed PDF if available
    if (selectedDocument.signed_pdf_url) {
      return `${process.env.REACT_APP_BACKEND_URL}${selectedDocument.signed_pdf_url}`;
    }
    
    // Use original PDF
    if (selectedDocument.file_url) {
      return `${process.env.REACT_APP_BACKEND_URL}${selectedDocument.file_url}`;
    }
    
    return null;
  };

  const getStatusBadge = (status) => {
    const config = {
      draft: { color: 'bg-slate-500', icon: FileText, label: 'Draft' },
      sent: { color: 'bg-blue-500', icon: Mail, label: 'Awaiting' },
      partially_signed: { color: 'bg-amber-500', icon: Clock, label: 'Partial' },
      completed: { color: 'bg-emerald-500', icon: CheckCircle2, label: 'Completed' }
    };
    const c = config[status] || config.draft;
    const Icon = c.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium text-white ${c.color}`}>
        <Icon className="h-3 w-3" />
        {c.label}
      </span>
    );
  };

  const displayWidth = 650;

  return (
    <div className="space-y-6" data-testid="documents-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-3">
            <FileSignature className="h-7 w-7 text-emerald-500" />
            Digital Signatures
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">
            Upload documents and collect e-signatures
          </p>
        </div>
        <Button
          onClick={() => setShowCreateModal(true)}
          className="bg-emerald-600 hover:bg-emerald-700"
          data-testid="new-document-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          New Document
        </Button>
      </div>

      {/* Tabs + Search */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
        {/* Tabs */}
        <div className="flex border-b border-slate-200 dark:border-slate-700">
          {[
            { id: 'awaiting', label: 'Awaiting Signature', icon: Clock, count: stats.awaiting },
            { id: 'completed', label: 'Completed', icon: CheckCircle2, count: stats.completed },
            { id: 'draft', label: 'Drafts', icon: FileText, count: stats.draft }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 py-4 px-6 text-sm font-medium transition-colors relative ${
                activeTab === tab.id
                  ? 'text-emerald-600 dark:text-emerald-400'
                  : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
              }`}
            >
              <div className="flex items-center justify-center gap-2">
                <tab.icon className="h-4 w-4" />
                {tab.label}
                <span className={`px-2 py-0.5 rounded-full text-xs ${
                  activeTab === tab.id ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300' : 'bg-slate-100 dark:bg-slate-700'
                }`}>
                  {tab.count}
                </span>
              </div>
              {activeTab === tab.id && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-emerald-500" />
              )}
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="p-4 border-b border-slate-200 dark:border-slate-700">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search documents, signers, or document type..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* Documents List */}
        {loading ? (
          <div className="p-8 text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto text-emerald-500" />
          </div>
        ) : filteredDocuments.length === 0 ? (
          <div className="p-12 text-center">
            <FileSignature className="h-16 w-16 mx-auto text-slate-300 dark:text-slate-600 mb-4" />
            <p className="text-lg text-slate-500 dark:text-slate-400">
              {searchQuery ? 'No documents match your search' : `No ${activeTab === 'awaiting' ? 'pending' : activeTab} documents`}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-slate-200 dark:divide-slate-700">
            {filteredDocuments.map(doc => (
              <div
                key={doc.document_id}
                className="p-4 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors cursor-pointer"
                onClick={() => viewDocument(doc)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3">
                      <h3 className="font-medium text-slate-900 dark:text-white truncate">
                        {doc.title}
                      </h3>
                      {getStatusBadge(doc.status)}
                    </div>
                    <div className="flex items-center gap-4 mt-2 text-sm text-slate-500">
                      <span className="flex items-center gap-1">
                        <Users className="h-4 w-4" />
                        {doc.signers?.length || 0} signer(s)
                      </span>
                      <span>
                        {doc.signers?.filter(s => s.status === 'signed').length || 0} / {doc.signers?.length || 0} signed
                      </span>
                      <span>{new Date(doc.created_at).toLocaleDateString()}</span>
                    </div>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {doc.signers?.slice(0, 3).map((s, idx) => (
                        <span
                          key={idx}
                          className={`text-xs px-2 py-0.5 rounded-full ${
                            s.status === 'signed'
                              ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300'
                              : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300'
                          }`}
                        >
                          {s.name} {s.status === 'signed' && '✓'}
                        </span>
                      ))}
                      {(doc.signers?.length || 0) > 3 && (
                        <span className="text-xs text-slate-400">+{doc.signers.length - 3} more</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-4" onClick={e => e.stopPropagation()}>
                    <Button variant="outline" size="sm" onClick={() => viewDocument(doc)}>
                      <Eye className="h-4 w-4" />
                    </Button>
                    {doc.status === 'draft' && (
                      <>
                        <Button
                          size="sm"
                          onClick={() => handleSendDocument(doc.document_id)}
                          disabled={sending}
                          className="bg-emerald-600 hover:bg-emerald-700"
                        >
                          <Send className="h-4 w-4 mr-1" />
                          Send
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeleteDocument(doc.document_id)}
                          className="text-red-600"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* CREATE DOCUMENT MODAL */}
      <Dialog open={showCreateModal} onOpenChange={(open) => !open && resetCreateModal()}>
        <DialogContent className="max-w-6xl max-h-[95vh] overflow-hidden flex flex-col p-0">
          <DialogHeader className="p-4 border-b border-slate-200 dark:border-slate-700">
            <DialogTitle className="flex items-center gap-2">
              <FileSignature className="h-5 w-5 text-emerald-500" />
              {!uploadedFile ? 'Upload Document' : 'Place Signature Fields'}
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-hidden flex">
            {!uploadedFile && (
              <div className="flex-1 p-8 flex items-center justify-center">
                <div
                  className="w-full max-w-xl border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-2xl p-12 text-center cursor-pointer hover:border-emerald-500 hover:bg-emerald-50/50 dark:hover:bg-emerald-900/10 transition-all"
                  onClick={() => fileInputRef.current?.click()}
                >
                  {uploading ? (
                    <div>
                      <Loader2 className="h-16 w-16 animate-spin mx-auto text-emerald-500" />
                      <p className="mt-4 text-lg text-slate-600">Uploading...</p>
                    </div>
                  ) : (
                    <>
                      <Upload className="h-16 w-16 mx-auto text-slate-400" />
                      <p className="mt-4 text-xl font-medium text-slate-700 dark:text-slate-300">
                        Drop your document here
                      </p>
                      <p className="text-slate-500 mt-2">or click to browse</p>
                      <p className="text-sm text-slate-400 mt-4">PDF, DOC, DOCX, PNG, JPG</p>
                    </>
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
                  onChange={handleFileUpload}
                  className="hidden"
                />
              </div>
            )}

            {uploadedFile && (
              <>
                <div className="w-72 border-r border-slate-200 dark:border-slate-700 p-4 overflow-auto bg-slate-50 dark:bg-slate-900">
                  <div className="space-y-4">
                    <div>
                      <Label className="text-xs text-slate-500">Title</Label>
                      <Input value={documentTitle} onChange={(e) => setDocumentTitle(e.target.value)} className="mt-1" />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Type</Label>
                      <Select value={documentType} onValueChange={setDocumentType}>
                        <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="contract">Contract</SelectItem>
                          <SelectItem value="offer_letter">Offer Letter</SelectItem>
                          <SelectItem value="nda">NDA</SelectItem>
                          <SelectItem value="sow">SOW</SelectItem>
                          <SelectItem value="agreement">Agreement</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Message</Label>
                      <Input value={documentMessage} onChange={(e) => setDocumentMessage(e.target.value)} className="mt-1" />
                    </div>
                    <div className="pt-4 border-t">
                      <div className="flex justify-between mb-3">
                        <h4 className="font-semibold text-sm">Signature Fields</h4>
                        <span className="text-xs text-slate-500">{signatureFields.length}</span>
                      </div>
                      <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 mb-3">
                        <p className="text-xs text-blue-700 dark:text-blue-300 flex items-start gap-2">
                          <MousePointer className="h-4 w-4 mt-0.5" />
                          Click on document to add signatures
                        </p>
                      </div>
                      {signatureFields.length === 0 ? (
                        <p className="text-sm text-slate-400 text-center py-4">No fields yet</p>
                      ) : (
                        <div className="space-y-2">
                          {signatureFields.map((field, idx) => (
                            <div key={field.id} className="flex items-center gap-2 p-2 rounded-lg bg-white dark:bg-slate-800 border">
                              <div className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs" style={{ backgroundColor: field.color }}>{idx + 1}</div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium truncate">{field.signerName}</p>
                                <p className="text-xs text-slate-500 truncate">{field.signerEmail}</p>
                              </div>
                              <button onClick={() => removeSignatureField(field.id)} className="text-red-500 p-1"><X className="h-4 w-4" /></button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex-1 flex flex-col bg-slate-100 dark:bg-slate-900">
                  <div className="flex items-center justify-center gap-4 p-3 border-b bg-white dark:bg-slate-800">
                    <Button variant="outline" size="sm" onClick={() => setCurrentPage(Math.max(1, currentPage - 1))} disabled={currentPage <= 1}>
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm font-medium">Page {currentPage} / {numPages}</span>
                    <Button variant="outline" size="sm" onClick={() => setCurrentPage(Math.min(numPages, currentPage + 1))} disabled={currentPage >= numPages}>
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="flex-1 overflow-auto p-4 flex justify-center">
                    <div ref={pdfContainerRef} className="relative bg-white shadow-xl cursor-crosshair" onClick={handlePdfClick}>
                      <Document file={`${process.env.REACT_APP_BACKEND_URL}${uploadedFile.file_url}`} onLoadSuccess={onDocumentLoadSuccess} loading={<Loader2 className="h-8 w-8 animate-spin mx-auto m-8" />}>
                        <Page pageNumber={currentPage} onLoadSuccess={onPageLoadSuccess} width={displayWidth} renderTextLayer={false} renderAnnotationLayer={false} />
                      </Document>
                      {signatureFields.filter(f => f.page === currentPage).map((field) => (
                        <div key={field.id} className="absolute border-2 rounded flex items-center justify-center pointer-events-none" style={{ left: `${field.x - field.width/2}%`, top: `${field.y - field.height/2}%`, width: `${field.width}%`, height: `${field.height}%`, borderColor: field.color, backgroundColor: `${field.color}20` }}>
                          <span className="text-xs font-medium truncate px-1" style={{ color: field.color }}>{field.signerName}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          {uploadedFile && (
            <DialogFooter className="p-4 border-t bg-white dark:bg-slate-800">
              <Button variant="outline" onClick={resetCreateModal}>Cancel</Button>
              <Button onClick={() => handleCreateDocument(false)} disabled={creating || signatureFields.length === 0} variant="outline">Save Draft</Button>
              <Button onClick={() => handleCreateDocument(true)} disabled={creating || signatureFields.length === 0} className="bg-emerald-600 hover:bg-emerald-700">
                {creating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                Send
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      {/* SIGNER POPUP */}
      <Dialog open={showSignerPopup} onOpenChange={setShowSignerPopup}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle><PenLine className="h-5 w-5 text-emerald-500 inline mr-2" />Add Signer</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Signer Name</Label>
              <Input value={newSignerName} onChange={(e) => setNewSignerName(e.target.value)} placeholder="John Smith" autoFocus />
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" value={newSignerEmail} onChange={(e) => setNewSignerEmail(e.target.value)} placeholder="john@company.com" onKeyDown={(e) => e.key === 'Enter' && handleAddSignatureField()} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSignerPopup(false)}>Cancel</Button>
            <Button onClick={handleAddSignatureField} className="bg-emerald-600 hover:bg-emerald-700"><Plus className="h-4 w-4 mr-2" />Add</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* SIGN LINKS MODAL */}
      <Dialog open={showSignLinksModal} onOpenChange={setShowSignLinksModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle><CheckCircle2 className="h-5 w-5 text-emerald-500 inline mr-2" />Document Sent!</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-slate-600 mb-4">Share these signing links:</p>
            <div className="space-y-3">
              {signLinks.map((link, idx) => (
                <div key={idx} className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                  <div className="flex justify-between mb-2">
                    <span className="font-medium">{link.name}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${link.status === 'sent' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                      {link.status === 'sent' ? 'Sent' : 'Share'}
                    </span>
                  </div>
                  <p className="text-sm text-slate-500 mb-2">{link.email}</p>
                  {link.sign_url && (
                    <div className="flex gap-2">
                      <Input value={link.sign_url} readOnly className="text-xs" />
                      <Button size="sm" variant="outline" onClick={() => copyToClipboard(link.sign_url)}><Copy className="h-4 w-4" /></Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setShowSignLinksModal(false)} className="bg-emerald-600 hover:bg-emerald-700">Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* VIEW DOCUMENT MODAL */}
      <Dialog open={showViewModal} onOpenChange={setShowViewModal}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col p-0">
          <DialogHeader className="p-4 border-b border-slate-200 dark:border-slate-700">
            <DialogTitle className="flex items-center gap-3">
              {selectedDocument?.title}
              {selectedDocument && getStatusBadge(selectedDocument.status)}
            </DialogTitle>
          </DialogHeader>

          {selectedDocument && (
            <div className="flex-1 overflow-hidden flex">
              {/* PDF Preview */}
              <div className="flex-1 flex flex-col bg-slate-100 dark:bg-slate-900">
                {/* Page nav + search */}
                <div className="flex items-center justify-between gap-4 p-3 border-b bg-white dark:bg-slate-800">
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => setViewPage(Math.max(1, viewPage - 1))} disabled={viewPage <= 1}>
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm font-medium">Page {viewPage} / {viewNumPages || '?'}</span>
                    <Button variant="outline" size="sm" onClick={() => setViewPage(Math.min(viewNumPages, viewPage + 1))} disabled={viewPage >= viewNumPages}>
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="relative w-64">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      placeholder="Search in document..."
                      value={documentSearch}
                      onChange={(e) => setDocumentSearch(e.target.value)}
                      className="pl-10 h-8 text-sm"
                    />
                  </div>
                </div>

                {/* PDF viewer */}
                <div className="flex-1 overflow-auto p-4 flex justify-center">
                  {getViewPdfUrl() ? (
                    <div className="bg-white shadow-xl">
                      <Document
                        file={getViewPdfUrl()}
                        onLoadSuccess={({ numPages }) => {
                          setViewNumPages(numPages);
                          setPdfLoading(false);
                          setPdfError(false);
                        }}
                        onLoadError={(error) => {
                          console.error('PDF load error:', error);
                          setPdfError(true);
                          setPdfLoading(false);
                        }}
                        loading={
                          <div className="p-12 text-center">
                            <Loader2 className="h-8 w-8 animate-spin mx-auto text-emerald-500" />
                            <p className="mt-2 text-slate-500">Loading document...</p>
                          </div>
                        }
                        error={
                          <div className="p-12 text-center">
                            <FileWarning className="h-12 w-12 mx-auto text-amber-500 mb-4" />
                            <p className="text-lg font-medium text-slate-700 dark:text-slate-300">Document Unavailable</p>
                            <p className="text-sm text-slate-500 mt-2">
                              The original document file could not be loaded.
                            </p>
                            <p className="text-xs text-slate-400 mt-4">
                              This may be a legacy document created before the file upload feature.
                            </p>
                          </div>
                        }
                      >
                        <Page
                          pageNumber={viewPage}
                          width={550}
                          renderTextLayer={!!documentSearch}
                          renderAnnotationLayer={false}
                        />
                      </Document>
                    </div>
                  ) : (
                    <div className="p-12 text-center bg-white rounded-lg shadow">
                      <FileWarning className="h-12 w-12 mx-auto text-amber-500 mb-4" />
                      <p className="text-lg font-medium text-slate-700">No Document File</p>
                      <p className="text-sm text-slate-500 mt-2">
                        This document does not have an associated file.
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Signers Sidebar */}
              <div className="w-72 border-l border-slate-200 dark:border-slate-700 p-4 overflow-auto">
                <h3 className="font-semibold mb-4">Signers</h3>
                <div className="space-y-3">
                  {selectedDocument.signers?.map((signer, idx) => (
                    <div key={signer.signer_id} className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium" style={{ backgroundColor: signer.color || SIGNER_COLORS[idx % SIGNER_COLORS.length] }}>
                          {idx + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{signer.name}</p>
                          <p className="text-xs text-slate-500 truncate">{signer.email}</p>
                        </div>
                      </div>
                      <div className="mt-2">
                        {signer.status === 'signed' ? (
                          <span className="text-xs text-emerald-600 flex items-center gap-1">
                            <CheckCircle2 className="h-3 w-3" />
                            Signed {signer.signed_at && new Date(signer.signed_at).toLocaleDateString()}
                          </span>
                        ) : (
                          <span className="text-xs text-amber-600 flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            Pending
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-6 pt-4 border-t">
                  <p className="text-xs text-slate-500">Created</p>
                  <p className="text-sm">{new Date(selectedDocument.created_at).toLocaleDateString()}</p>
                  {selectedDocument.completed_at && (
                    <>
                      <p className="text-xs text-slate-500 mt-2">Completed</p>
                      <p className="text-sm">{new Date(selectedDocument.completed_at).toLocaleDateString()}</p>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
